import React from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { NO_IMAGE } from "../../../assets";
import { BusinessHomeProfile } from "../../../models/businessHomeProfile";
import { AdmissionChecklistSection, AdmissionsSection, CareServicesSection, ComplianceSection, ProviderAboutSection, RoomsSection } from "../../../models/manageWebSite";
import { getWebsiteTemplateOption } from "./websiteTemplates";

type WebsitePageLink = {
  key: string;
  label: string;
  href: string;
  active?: boolean;
};

type Props = {
  templateKey?: string | null;
  businessData: BusinessHomeProfile | null;
  roomsData: RoomsSection | null;
  complianceData: ComplianceSection | null;
  careServicesData: CareServicesSection | null;
  providerAboutData: ProviderAboutSection | null;
  admissionsData: AdmissionsSection | null;
  admissionChecklistData: AdmissionChecklistSection | null;
  pageSlug: "rooms" | "compliance" | "care-services" | "about" | "admissions" | "admission-checklist";
  pageLinks: WebsitePageLink[];
  onOpenInquiry?: () => void;
  isPreview?: boolean;
};

const pageShellStyle = {
  maxWidth: "1180px",
  margin: "0 auto",
  padding: "1.25rem 1rem 2rem",
} as const;

const WebsiteSubpageRenderer = ({
  templateKey,
  businessData,
  roomsData,
  complianceData,
  careServicesData,
  providerAboutData,
  admissionsData,
  admissionChecklistData,
  pageSlug,
  pageLinks,
  onOpenInquiry,
  isPreview = false,
}: Props) => {
  const template = getWebsiteTemplateOption(templateKey);
  const accent = template.accentColor;
  const [roomLightboxVisible, setRoomLightboxVisible] = React.useState(false);
  const [activeRoomImageIndex, setActiveRoomImageIndex] = React.useState(0);
  const pageData =
    pageSlug === "rooms"
      ? roomsData
      : pageSlug === "compliance"
        ? complianceData
        : pageSlug === "care-services"
          ? careServicesData
          : pageSlug === "about"
            ? providerAboutData
            : pageSlug === "admission-checklist"
              ? admissionChecklistData
              : admissionsData;
  const roomGalleryImages =
    ((roomsData?.roomEntries || []).flatMap((room) => room.signedImageUrls || []).length > 0
      ? (roomsData?.roomEntries || []).flatMap((room) => room.signedImageUrls || [])
      : roomsData?.sectionSignedImg || []);

  const openRoomLightbox = (index: number) => {
    setActiveRoomImageIndex(index);
    setRoomLightboxVisible(true);
  };

  const showPreviousRoomImage = () => {
    if (roomGalleryImages.length === 0) {
      return;
    }
    setActiveRoomImageIndex((prev) => (prev - 1 + roomGalleryImages.length) % roomGalleryImages.length);
  };

  const showNextRoomImage = () => {
    if (roomGalleryImages.length === 0) {
      return;
    }
    setActiveRoomImageIndex((prev) => (prev + 1) % roomGalleryImages.length);
  };

  React.useEffect(() => {
    if (!roomLightboxVisible) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        showPreviousRoomImage();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        showNextRoomImage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [roomLightboxVisible, roomGalleryImages.length]);

  const renderCtaButton = () => {
    if (isPreview || !onOpenInquiry) {
      return null;
    }

    return (
      <button
        type="button"
        onClick={onOpenInquiry}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.55rem",
          background: "#ffffff",
          color: accent,
          border: "1px solid rgba(15,23,42,0.08)",
          fontWeight: 700,
          borderRadius: "999px",
          padding: "0.8rem 1.15rem",
          cursor: "pointer",
          fontSize: "0.98rem",
          lineHeight: 1,
          boxShadow: "0 12px 24px rgba(15,23,42,0.08)",
        }}
      >
        <i className="pi pi-send" style={{ color: accent, fontSize: "0.95rem" }} />
        <span style={{ color: accent }}>Request Information</span>
      </button>
    );
  };

  let pageContent: React.ReactNode;

  if (pageSlug === "rooms") {
    pageContent = (
      <div className="grid">
        <div className="col-12 lg:col-8">
          <div style={{ background: "#ffffff", border: "1px solid #dbe7f3", borderRadius: "30px", padding: "1.5rem", boxShadow: "0 20px 40px rgba(15,61,99,0.08)" }}>
            <div className="grid">
              {(roomsData?.roomEntries && roomsData.roomEntries.length > 0 ? roomsData.roomEntries : []).map((room) => (
                <div className="col-12 md:col-6" key={room.roomId}>
                  <div style={{ background: "#f8fbff", border: "1px solid #dbe7f3", borderRadius: "24px", padding: "1rem", height: "100%" }}>
                    <div className="flex align-items-start justify-content-between gap-2 flex-wrap">
                      <div>
                        <div style={{ color: accent, fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: "0.1em" }}>{room.spaceType || "Room"}</div>
                        <div style={{ color: "#0f172a", fontSize: "1.15rem", fontWeight: 800, marginTop: "0.45rem" }}>{room.name}</div>
                      </div>
                      <span style={{ background: "#ffffff", border: "1px solid #dbe7f3", borderRadius: "999px", padding: "0.3rem 0.65rem", color: "#475569", fontSize: "0.8rem", fontWeight: 700 }}>
                        {(room.signedImageUrls || []).length} photos
                      </span>
                    </div>
                    {(room.bedroomOption || room.bathroomOption) ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {room.bedroomOption ? <span style={{ background: "#eff6ff", color: accent, borderRadius: "999px", padding: "0.35rem 0.7rem", fontWeight: 700, fontSize: "0.8rem" }}>{room.bedroomOption}</span> : null}
                        {room.bathroomOption ? <span style={{ background: "#fff7ed", color: "#b45309", borderRadius: "999px", padding: "0.35rem 0.7rem", fontWeight: 700, fontSize: "0.8rem" }}>{room.bathroomOption}</span> : null}
                      </div>
                    ) : null}
                    {room.description ? (
                      <div style={{ color: "#475569", lineHeight: 1.8, marginTop: "0.85rem" }}>{room.description}</div>
                    ) : null}
                    {(room.amenities || []).length ? (
                      <ul style={{ paddingLeft: "1rem", margin: "0.9rem 0 0", lineHeight: 1.9, color: "#475569" }}>
                        {(room.amenities || []).map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
                      </ul>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            {roomsData?.pricingNotes ? (
              <div style={{ marginTop: "1rem", background: "#ffffff", border: `1px dashed ${accent}`, borderRadius: "20px", padding: "1rem", color: "#475569", lineHeight: 1.8 }}>
                <strong style={{ color: "#0f172a" }}>Availability and pricing:</strong> {roomsData.pricingNotes}
              </div>
            ) : null}
          </div>
        </div>
        <div className="col-12 lg:col-4">
          <div style={{ background: "#ffffff", border: "1px solid #dbe7f3", borderRadius: "30px", padding: "1.25rem", boxShadow: "0 20px 40px rgba(15,61,99,0.08)" }}>
            <div style={{ color: "#0f172a", fontSize: "1.2rem", fontWeight: 800 }}>Room gallery</div>
            <div className="grid mt-2">
              {roomGalleryImages.map((image, index) => (
                <div className="col-12 sm:col-6" key={`${image}-${index}`}>
                  <div
                    onClick={() => openRoomLightbox(index)}
                    style={{ minHeight: "180px", borderRadius: "20px", border: "1px solid #dbe7f3", background: `url(${image}) center/cover no-repeat`, cursor: "pointer" }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  } else if (pageSlug === "compliance") {
    pageContent = (
      <div className="grid">
        <div className="col-12 lg:col-7">
          <div style={{ background: "#ffffff", border: "1px solid #f0dcc2", borderRadius: "30px", padding: "1.5rem", boxShadow: "0 20px 40px rgba(180,83,9,0.08)" }}>
            <div className="grid">
              <div className="col-12 md:col-6">
                <div style={{ background: "#fffaf3", border: "1px solid #f0dcc2", borderRadius: "24px", padding: "1rem", height: "100%" }}>
                  <div style={{ color: "#b45309", fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: "0.1em" }}>Washington AFH licensing</div>
                  <div className="mt-2" style={{ color: "#6c4b22", lineHeight: 1.9 }}>
                    <div><strong>License number:</strong> {complianceData?.licenseNumber || "Available on request"}</div>
                    <div><strong>Status:</strong> {complianceData?.licenseStatus || "Active"}</div>
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-6">
                <div style={{ background: "#f8fbff", border: "1px solid #dbe7f3", borderRadius: "24px", padding: "1rem", height: "100%" }}>
                  <div style={{ color: accent, fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: "0.1em" }}>Care coordination support</div>
                  <div className="mt-2" style={{ color: "#36546c", lineHeight: 1.9 }}>
                    <div><strong>Nurse delegation:</strong> {complianceData?.nurseDelegationSupport}</div>
                    <div className="mt-2"><strong>Medication support:</strong> {complianceData?.medicationManagementSupport}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid mt-2">
              <div className="col-12 md:col-6">
                <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "24px", padding: "1rem" }}>
                  <div style={{ color: "#0f172a", fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: "0.1em" }}>Certifications</div>
                  <ul style={{ paddingLeft: "1rem", margin: "0.9rem 0 0", lineHeight: 1.9, color: "#475569" }}>
                    {(complianceData?.certifications || []).map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
                  </ul>
                </div>
              </div>
              <div className="col-12 md:col-6">
                <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "24px", padding: "1rem" }}>
                  <div style={{ color: "#0f172a", fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: "0.1em" }}>Training highlights</div>
                  <ul style={{ paddingLeft: "1rem", margin: "0.9rem 0 0", lineHeight: 1.9, color: "#475569" }}>
                    {(complianceData?.trainingHighlights || []).map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 lg:col-5">
          <div style={{ background: "#ffffff", border: "1px solid #f0dcc2", borderRadius: "30px", padding: "1.5rem", boxShadow: "0 20px 40px rgba(180,83,9,0.08)", height: "100%" }}>
            <div style={{ color: "#0f172a", fontSize: "1.2rem", fontWeight: 800 }}>Compliance notes</div>
            <div style={{ color: "#475569", lineHeight: 1.9, marginTop: "0.85rem" }}>{complianceData?.complianceNotes}</div>
            <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid #f3e8d3", color: "#64748b", lineHeight: 1.9 }}>
              <strong style={{ color: "#0f172a" }}>Background checks and team readiness</strong>
              <div className="mt-2">{complianceData?.backgroundCheckSummary}</div>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (pageSlug === "care-services") {
    pageContent = (
      <div className="grid">
        {[
          { title: "Specialized care", items: careServicesData?.specializedCare, color: accent, border: "#dbe7f3", bg: "#f8fbff" },
          { title: "Meals and nutrition", items: careServicesData?.mealsAndNutrition, color: "#b45309", border: "#f0dcc2", bg: "#fffaf3" },
          { title: "Daily life", items: careServicesData?.dailyLifeHighlights, color: "#0f172a", border: "#e2e8f0", bg: "#f8fafc" },
          { title: "Care team", items: careServicesData?.careTeam, color: accent, border: "#dbe7f3", bg: "#ffffff" },
        ].map((card) => (
          <div className="col-12 md:col-6" key={card.title}>
            <div style={{ background: card.bg, border: `1px solid ${card.border}`, borderRadius: "24px", padding: "1.2rem", height: "100%", boxShadow: "0 20px 40px rgba(15,61,99,0.08)" }}>
              <div style={{ color: card.color, fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: "0.1em" }}>{card.title}</div>
              <ul style={{ paddingLeft: "1rem", margin: "0.9rem 0 0", lineHeight: 1.9, color: "#475569" }}>
                {(card.items || []).map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
              </ul>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (pageSlug === "about") {
    pageContent = (
      <div className="grid">
        <div className="col-12 lg:col-6">
          <div style={{ background: "#ffffff", border: "1px solid #f3d5e6", borderRadius: "30px", padding: "1.5rem", boxShadow: "0 20px 40px rgba(190,24,93,0.08)", height: "100%" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "1.25rem" }}>
              <img
                src={providerAboutData?.providerSignedImg || NO_IMAGE}
                alt={businessData?.businessName ? `${businessData.businessName} provider` : "Provider"}
                style={{ width: "100%", maxWidth: "280px", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: "26px", border: "1px solid #f3d5e6" }}
              />
            </div>
            <div style={{ color: "#be185d", fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: "0.1em" }}>Meet the provider</div>
            <div style={{ color: "#475569", lineHeight: 1.9, marginTop: "0.9rem", whiteSpace: "pre-line" }}>{providerAboutData?.meetTheProvider}</div>
            <div style={{ color: "#be185d", fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: "0.1em", marginTop: "1.2rem" }}>Care philosophy</div>
            <div style={{ color: "#475569", lineHeight: 1.9, marginTop: "0.9rem", whiteSpace: "pre-line" }}>{providerAboutData?.carePhilosophy}</div>
          </div>
        </div>
        <div className="col-12 lg:col-6">
          <div style={{ background: "#ffffff", border: "1px solid #f3d5e6", borderRadius: "30px", padding: "1.5rem", boxShadow: "0 20px 40px rgba(190,24,93,0.08)", height: "100%" }}>
            <div style={{ color: "#be185d", fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: "0.1em" }}>Why families choose us</div>
            <ul style={{ paddingLeft: "1rem", margin: "0.9rem 0 0", lineHeight: 1.9, color: "#475569" }}>
              {(providerAboutData?.whyFamiliesChooseUs || []).map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
            </ul>
            <div style={{ color: "#be185d", fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: "0.1em", marginTop: "1.2rem" }}>Testimonials</div>
            <ul style={{ paddingLeft: "1rem", margin: "0.9rem 0 0", lineHeight: 1.9, color: "#475569" }}>
              {(providerAboutData?.testimonials || []).map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
            </ul>
          </div>
        </div>
      </div>
    );
  } else {
    pageContent = pageSlug === "admission-checklist" ? (
      <div className="grid">
        {(admissionChecklistData?.checklistCategories || []).map((category, index) => (
          <div className="col-12 md:col-6 lg:col-4" key={`${category.title}-${index}`}>
            <div style={{ background: "#ffffff", border: "1px solid #dbe7f3", borderRadius: "24px", padding: "1.2rem", height: "100%", boxShadow: "0 20px 40px rgba(15,61,99,0.08)" }}>
              <div style={{ color: accent, fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: "0.1em" }}>{category.title}</div>
              <ul style={{ paddingLeft: "1rem", margin: "0.9rem 0 0", lineHeight: 1.9, color: "#475569" }}>
                {(category.items || []).map((item, itemIndex) => <li key={`${item}-${itemIndex}`}>{item}</li>)}
              </ul>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="grid">
        <div className="col-12">
          <div style={{ background: "#ffffff", border: "1px solid #f0dcc2", borderRadius: "30px", padding: "1.5rem", boxShadow: "0 20px 40px rgba(180,83,9,0.08)", height: "100%" }}>
            <div style={{ color: "#b45309", fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: "0.1em" }}>Frequently Asked Questions</div>
            <div className="grid mt-3">
              {(admissionsData?.faqs || []).map((item, index) => (
                <div className="col-12 md:col-6" key={`${item.question}-${index}`}>
                  <div style={{ background: "#fffaf3", border: "1px solid #f0dcc2", borderRadius: "20px", padding: "1.1rem", height: "100%" }}>
                    <div style={{ color: "#92400e", fontWeight: 800, lineHeight: 1.5 }}>Q. {item.question}</div>
                    <div style={{ color: "#475569", marginTop: "0.75rem", lineHeight: 1.85 }}>A. {item.answer}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)", minHeight: "100vh" }}>
      <div
        style={{
          background: `linear-gradient(135deg, ${accent} 0%, #0f172a 100%)`,
          color: "#ffffff",
          padding: "1rem 1rem 2.5rem",
        }}
      >
        <div style={{ ...pageShellStyle, paddingBottom: "0.5rem" }}>
          <div className="flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <div style={{ fontSize: "2.1rem", fontWeight: 800 }}>{pageData?.title || businessData?.businessName}</div>
              <div style={{ color: "rgba(255,255,255,0.88)", marginTop: "0.8rem", lineHeight: 1.8, maxWidth: "760px" }}>
                {pageData?.description}
              </div>
            </div>
            <div>{renderCtaButton()}</div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {pageLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                style={{
                  borderRadius: "999px",
                  padding: "0.65rem 0.95rem",
                  fontWeight: 700,
                  border: link.active ? "none" : "1px solid rgba(255,255,255,0.24)",
                  background: link.active ? "#ffffff" : "rgba(255,255,255,0.12)",
                  color: link.active ? accent : "#ffffff",
                  textDecoration: "none",
                }}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      <div style={pageShellStyle}>
        {pageContent}
      </div>
      <Dialog
        visible={roomLightboxVisible}
        onHide={() => setRoomLightboxVisible(false)}
        maximized
        focusOnShow={false}
        style={{ background: "#020617" }}
        contentStyle={{ background: "#020617", padding: 0 }}
        headerStyle={{ background: "#020617", color: "#ffffff", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        header={roomGalleryImages.length > 0 ? `Room Gallery ${activeRoomImageIndex + 1} of ${roomGalleryImages.length}` : "Room Gallery"}
      >
        {roomGalleryImages[activeRoomImageIndex] ? (
          <div
            className="flex align-items-center justify-content-center"
            style={{ minHeight: "calc(100vh - 8rem)", gap: "1rem", padding: "1rem", background: "#020617" }}
          >
            <Button icon="pi pi-chevron-left" rounded outlined severity="contrast" autoFocus onClick={showPreviousRoomImage} />
            <div className="flex align-items-center justify-content-center" style={{ flex: 1, height: "100%" }}>
              <img
                src={roomGalleryImages[activeRoomImageIndex]}
                alt={`Room Gallery ${activeRoomImageIndex + 1}`}
                style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: "18px" }}
              />
            </div>
            <Button icon="pi pi-chevron-right" rounded outlined severity="contrast" onClick={showNextRoomImage} />
          </div>
        ) : null}
      </Dialog>
    </div>
  );
};

export default WebsiteSubpageRenderer;
